import BaseModel from 'volta/models/base-model';
import { _normalize, collectionCommand, resourceCommand as command } from 'volta/utils/api/index';

import { attr, hasMany } from '@ember-data/model';
import { isBlank } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import { Type } from '@warp-drive/core-types/symbols';

import type Supplier from 'volta/models/supplier';
import type UserRole from 'volta/models/user-role';
import type Warehouse from 'volta/models/warehouse';
import type {
  ICreateUserCmd,
  IResetUserPasswordCmd,
  IResetUserPasswordResult,
  IUpdateUserCmd,
  IUpdateUserPasswordCmd,
  IUpdateUserProfileCmd,
  TLoginInfos,
  TUserType
} from 'volta/models/types/user';
import type UserPreferences from 'volta/models/user-preference';
import type Workshop from 'volta/models/workshop';

type TUserName = Pick<User, 'firstName' | 'middleName' | 'lastName' | 'login' | 'email'>;

export const getFullName = (user: TUserName) => {
  const middleName = `${user.middleName ? ' ' + user.middleName + ' ' : ''}`;
  return `${user.firstName ?? ''}${middleName} ${user.lastName ?? ''}`.trim();
};

export const getDisplayName = (user: TUserName) => {
  const fullName = getFullName(user);
  return isBlank(fullName) ? user.login : fullName;
};

export const MeUserId = 'ffffffff-ffff-ffff-ffff-ffffffffffff';
export const UserCommands = {
  CreateUser: 'CreateUser',
  ResetUserPassword: 'ResetUserPassword',
  UpdateUserProfile: 'UpdateUserProfile',
  UpdateUserPassword: 'UpdateUserPassword',
  UpdateUserPreferences: 'UpdateUserPreferences',
  UpdateUser: 'UpdateUser',
  ActivateUser: 'ActivateUser',
  DeactivateUser: 'DeactivateUser',
  ResetUserPersonalAccessToken: 'ResetUserPersonalAccessToken'
};

export const UserResourceName = {
  singular: 'user',
  plural: 'users'
};

export interface IPersonalAccessToken {
  expirationDate?: string; // Format YYYY-MM-DD
  createdAt: string; // Format YYYY-MM-DD
}

export default class User extends BaseModel {
  /**
   * Collection commands
   */

  static createUser = collectionCommand<ICreateUserCmd, User, typeof User>(UserCommands.CreateUser);

  static resetUserPassword = collectionCommand<
    IResetUserPasswordCmd,
    IResetUserPasswordResult,
    typeof User
  >(UserCommands.ResetUserPassword);

  static resetUserPersonalAccessToken = collectionCommand<any, any, typeof User>(
    UserCommands.ResetUserPersonalAccessToken
  );

  /**
   * Resource commands
   */

  activateUser = command<void, User>(UserCommands.ActivateUser);
  deactivateUser = command<void, User>(UserCommands.DeactivateUser);
  updateProfile = command<IUpdateUserProfileCmd, User>(UserCommands.UpdateUserProfile);
  updatePassword = command<IUpdateUserPasswordCmd, User>(UserCommands.UpdateUserPassword);
  updateUser = command<IUpdateUserCmd, User>(UserCommands.UpdateUser);

  /**
   * Attributes
   */

  @attr('string') login!: string;
  @attr('string') firstName?: string;
  @attr('string') middleName?: string;
  @attr('string') lastName?: string;
  @attr('string') email?: string;
  @attr('boolean', { defaultValue: false }) connected!: boolean;
  @attr('date') deletedAt: Date | null | undefined;
  @attr('string', { defaultValue: 'REGULAR' }) userType!: TUserType;

  @attr({
    defaultValue: () => {
      return {};
    }
  })
  loginInfos!: TLoginInfos;

  @attr()
  personalAccessToken!: IPersonalAccessToken;

  @hasMany('warehouse', { async: false, inverse: null })
  warehouses!: Warehouse[];

  @hasMany('workshop', { async: false, inverse: null })
  workshops!: Workshop[];

  @hasMany('supplier', { async: false, inverse: null })
  suppliers!: Supplier[];

  @hasMany('user-role', { async: false, inverse: null })
  roles!: UserRole[];

  /**
   * Will be populated by the session-user service on app init
   */
  @tracked
  declare preferences: UserPreferences;

  /**
   * Getters
   */

  get userId() {
    return this.id;
  }

  /**
   * Aggregated user roles permissions
   */
  get permissions() {
    return this.roles.reduce((acc: string[], role: UserRole) => {
      role.permissions.forEach((permission: string) => {
        if (!acc.includes(permission)) {
          acc.push(permission);
        }
      });
      return acc;
    }, []);
  }

  get fullName() {
    return getFullName(this);
  }

  get displayName() {
    return getDisplayName(this);
  }

  get warehouseIds(): string[] {
    return this.warehouses.map((s) => s.id!).sort();
  }

  get workshopIds() {
    return this.workshops.map((s) => s.id!).sort();
  }

  get supplierIds(): string[] {
    return this.suppliers.map((s) => s.id!).sort();
  }

  get roleIds(): string[] {
    return this.roles.map((s) => s.id!).sort();
  }

  get isStaff() {
    return this.userType === 'STAFF';
  }

  get isSupplier() {
    return this.userType === 'SUPPLIER';
  }

  get isActive() {
    return this.deletedAt === undefined || this.deletedAt === null;
  }

  get isInactive() {
    return !this.isActive;
  }

  [Type] = 'user' as const;
}
